import type {
  CreateFlowApiArg,
  EmailIntegration,
  Flow,
  Queue,
} from 'lib-shared-types-api';
import getSlug from 'speakingurl';
import { v4 as uuid } from 'uuid';

import { sanitizeHtml } from 'utils/sanitizeHtml';

import { EmailModalState, EmailModalType } from '../emailModal.types';

export const defaultEmailSignature = `<p>
Best regards,
</p>
<p>
{{agent_fullname}}<br />
{{organization_name}}
</p>`;

export const signatureHelpText =
  `Custom signature will overwrite the email default signature.:\n\n
Best regards,
{{agent_fullname}}
{{organization_name}}\n\n` +
  `You can use these replacing variables eg. "*{{agent_fullname}}*" with the actual agent name.:\n\n
{{organization_name}}
{{requester_firstname}}
{{requester_fullname}}
{{requester_email}}
{{agent_firstname}}
{{agent_fullname}}
{{conversation_subject}}
{{csid}}\n\n` +
  `_note_: csid is the conversation ID.`;

/**
 * Gets the title for the Modal.
 *
 * @param type The modal type.
 * @param emailAddress The email address.
 */
export function getTitle(
  type: EmailModalType,
  emailAddress?: EmailIntegration['email_address'],
): string {
  switch (type) {
    case 'create':
      return 'Add new email';
    case 'duplicate':
      return `Duplicate ${emailAddress}`;
    case 'update':
      return 'Settings';
  }
}

/**
 * Gets the email identifier.
 *
 * @param emailAddress The email address.
 * @param prefix The email prefix.
 */
export function getEmailIdentifier(
  emailAddress: EmailIntegration['email_address'],
  prefix: string,
): string {
  return emailAddress.slice(prefix.length, emailAddress.indexOf('@'));
}

/**
 * Gets the domain part of the email address.
 *
 * @param emailAddress The email address.
 */
export function getEmailDomain(
  emailAddress: EmailIntegration['email_address'],
): string {
  return emailAddress.slice(emailAddress.indexOf('@') + 1);
}

/**
 * Gets the payload for creating an email.
 *
 * @param param The email modal state.
 */
export function createEmailPayload({
  identifier,
  senderName,
  signatureTemplate,
  domain,
}: EmailModalState): Pick<
  EmailIntegration,
  'sender_name' | 'signature_template'
> & {
  identifier: string;
  domain: string;
} {
  return {
    domain,
    identifier: getSlug(identifier),
    sender_name: senderName?.trim() || null,
    signature_template:
      (signatureTemplate &&
        sanitizeHtml(
          getTrimmedSignatureTemplate(signatureTemplate) ?? '',
          'email',
        )) ??
      null,
  };
}

/**
 * Returns the trimmed signature template or `null`.
 *
 * @param template The email signature template
 */
export function getTrimmedSignatureTemplate(
  template: EmailModalState['signatureTemplate'],
): string | null {
  return template.trim() !== '' &&
    template.trim() !== '<p></p>' &&
    template.trim() !== '<p>&nbsp;</p>'
    ? template.trim()
    : null;
}

/**
 * Returns the email base flow for new email addresses.
 *
 * @param emailAddress The email address.
 * @param queue The default queue.
 */
export function getEmailBaseFlow(
  emailAddress: EmailIntegration['email_address'],
  queue: Queue,
): CreateFlowApiArg {
  const initNodeId = uuid();
  const queueNodeId = uuid();

  return {
    name: `Email flow for ${emailAddress}`,
    location: emailAddress,
    channel: 'email',
    active: true,
    actions: [
      {
        childIds: [queueNodeId],
        connectionLabel: 'arrow',
        id: initNodeId,
        isEditable: false,
        isRemovable: false,
        nodeType: 'init',
        pageX: 0,
        pageY: 0,
        parentId: null,
        payload: null,
      },
      {
        connectionLabel: 'arrow',
        childIds: [],
        id: queueNodeId,
        isEditable: true,
        isRemovable: true,
        nodeType: 'queue',
        pageX: 0,
        pageY: 150,
        parentId: initNodeId,
        payload: {
          queue: {
            value: queue.id,
            label: queue.name,
          },
        },
      },
    ],
  };
}

/**
 * Generates new email flow for duplication.
 *
 * @param emailAddress The email address.
 * @param flow The email flow.
 */
export function getNewEmailFlow(
  emailAddress: EmailIntegration['email_address'],
  flow: Flow,
): CreateFlowApiArg {
  return {
    actions: flow.actions,
    active: true,
    channel: 'email',
    location: emailAddress,
    name: emailAddress,
  };
}
